import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IFilterJob, IJobCities, IJobFilters } from 'core/interface/job';
import useLocales from '../../../hooks/useLocales';
import { JobService } from 'services/jobs/job';
import { getCareerLevelLabels, getSalaryLabels } from 'constants/constants';
import Checkboxes from 'components/shared/Checkbox/Index';
import Autocomplete from 'components/shared/Autocomplete/Index';
import Button from 'components/Button/Index';
import { ProfileService } from 'services/profile/profile';
import DropDown from 'components/Dropdown';
import {
  degreeTypes,
  companySizeChoices
} from 'core/constants/aboutMePopUpConstants';
import { RootState } from 'redux/store';
import { ILocationRedux, IndustryRedux } from 'core/interface/education';
import { SkillService } from 'services/skills/skillService';
import { SearchInitialValue } from 'core/interface/searchInitialValue';
import { Industries } from 'core/interface/education-side';
import { IAutocompleteItem } from 'core/interface/autocompleteItem';
import { Form, Formik } from 'formik';
import Multiselect from 'components/shared/Multiselect/Index';
import Image from 'components/image/Image';
import { contractTypes } from 'core/constants/workType';
import {
  hourlyRateChoices,
  monthlySalary
} from 'core/constants/preferenceConstats';

import styles from './index.module.scss';
import { LanguageService } from 'services/language';
import { ICandidateLanguage } from 'core/interface/candidate-language';

const searchInitialValue: SearchInitialValue<
  Industries[] | IAutocompleteItem[] | IJobCities | null
> = {
  industry: {
    value: '',
    interval: null,
    func: (query: string) => ProfileService.getIndustries(query)
  },
  computer_skill: {
    value: '',
    interval: null,
    func: (query: string) => SkillService.getITSkillsList(query)
  },
  location: {
    value: '',
    interval: null,
    func: (query: string) =>
      JobService.getLocations({
        q: query
      })
  }
};
const JobFilters: FC<IFilterJob> = ({ applyFilter }): JSX.Element => {
  const { t } = useLocales();
  const industries = useSelector(
    (selector: IndustryRedux) => selector.industries?.industry || []
  );
  const { computerSkills: skills } = useSelector(
    (selector: RootState) => selector.computerSkills || []
  );
  const cities = useSelector(
    (selector: ILocationRedux) => selector.locations?.cities || []
  );

  const { languages } = useSelector((state: RootState) => state.languages);

  const [resetDropdown, setResetDropdown] = useState<boolean>(false);
  const [values, setValues] = useState<IJobFilters>({
    industry: '',
    degree_type: '',
    computer_skill: [],
    location: {
      id: 0,
      name: ''
    },
    compensations: [],
    career_level: [],
    languages: [] as ICandidateLanguage[],
    is_remote: false,
    contract: [],
    company_sizes: []
  });

  const [search, setSearch] =
    useState<
      SearchInitialValue<
        Industries[] | IAutocompleteItem[] | IJobCities | IJobCities | null
      >
    >(searchInitialValue);

  const handleCheckbox = (e: string, key: string): void => {
    const value = values[key] as string[];
    const index = value.indexOf(e);
    if (index !== -1) {
      value.splice(index, 1);
    } else {
      value.push(e);
    }

    setValues({
      ...values,
      [key]: value
    });
  };

  const onInputChange = (e: string, type: string): void => {
    const searchNewData = { ...searchInitialValue };
    searchNewData[type].value = e;
    if (searchNewData[type].interval) {
      clearTimeout(searchNewData[type].interval as NodeJS.Timeout);
    }
    searchNewData[type].interval = setTimeout(() => {
      searchNewData[type].func(searchNewData[type].value);
    }, 500);
    setSearch(searchNewData);
  };

  useEffect(() => {
    Object.values(search).map((e) => {
      e.func(e.value);
    });
    // Fetch languages on component mount
    getLanguages();
  }, []);

  const handleFilter = (): void => {
    const val = {
      ...values
    };
    applyFilter(val);
  };

  const handleLoadMoreCities = () => {
    const params = { limit: 10, offset: cities.length };
    JobService.getLocations(params, true);
  };

  const handleResetFilters = () => {
    const val = {
      industry: '',
      degree_type: '',
      computer_skill: [],
      location: {
        id: 0,
        name: ''
      },
      compensations: [],
      career_level: [],
      languages: [] as ICandidateLanguage[],
      is_remote: false,
      contract: [],
      company_sizes: []
    };

    setValues(val);
    applyFilter(val);
    setResetDropdown(true);
    setSearch({
      industry: {
        value: '',
        interval: null,
        func: (query: string) => ProfileService.getIndustries(query)
      },
      computer_skill: {
        value: '',
        interval: null,
        func: (query: string) => SkillService.getITSkillsList(query)
      },
      location: {
        value: '',
        interval: null,
        func: (query: string) =>
          JobService.getLocations({
            q: query
          })
      }
    });

    // Reset all dropdowns and multiselects by forcing a re-render
    setResetDropdown(false);
    setTimeout(() => {
      setResetDropdown(true);
    }, 0);
  };

  const handleComputerSkillChange = (
    computerSkill: string | IAutocompleteItem[]
  ): void => {
    if (typeof computerSkill != 'string' && computerSkill) {
      const newSkills = computerSkill
        .map((skill) => {
          if (typeof skill === 'object' && 'id' in skill) {
            return String(skill.id);
          }
          const foundSkill = skills?.find((s) => s.name === skill);
          return String(foundSkill?.id || '');
        })
        .filter((id) => id !== '' && !values.computer_skill.includes(id));

      setValues((prev) => ({
        ...prev,
        computer_skill: [...prev.computer_skill, ...newSkills]
      }));
    }
  };

  const getLanguages = (q?: string) => {
    LanguageService.getAllLanguages(q || '');
  };

  const handleRemoveLanguage = (item: ICandidateLanguage): void => {
    const updatedValues = {
      ...values,
      languages: values?.languages.filter(
        (e: ICandidateLanguage) => e.id !== item.id
      )
    };
    setValues(updatedValues);
    applyFilter(updatedValues);
  };

  const handleClearIndustry = () => {
    const updatedValues = {
      ...values,
      industry: ''
    };
    setValues(updatedValues);
    applyFilter(updatedValues);
  };

  const handleClearDegreeType = () => {
    const updatedValues = {
      ...values,
      degree_type: ''
    };
    setValues(updatedValues);
    applyFilter(updatedValues);
  };

  const handleClearLocation = () => {
    const updatedValues = {
      ...values,
      location: {
        id: 0,
        name: ''
      }
    };
    setValues(updatedValues);
    applyFilter(updatedValues);
  };

  // Helper function to get compensations labels based on contract type
  const getSalaryLabelsByContractType = (
    compensations: string[],
    contractTypes: string[]
  ) => {
    // If contract includes "2" (Freelance), show hourly rate choices
    if (contractTypes.includes('2')) {
      return hourlyRateChoices.map((choice) => [
        {
          label: String(choice.to_show),
          isChecked: compensations?.includes(String(choice.value)),
          value: String(choice.value)
        }
      ]);
    }
    // Otherwise show regular compensations labels
    return getSalaryLabels(compensations);
  };

  // Function to render compensation section with appropriate headers
  const renderCompensationSection = () => {
    const salaryLabels = getSalaryLabelsByContractType(
      values.compensations,
      values.contract
    );
    // Only show both types if freelance is selected along with other contract types
    const showBothTypes =
      values.contract.includes('2') && values.contract.length > 1;
    // Show only hourly rates if only freelance is selected
    const showOnlyHourlyRates =
      values.contract.includes('2') && values.contract.length === 1;
    // Show monthly salary by default or if non-freelance contract types are selected
    const showMonthlySalary = !showOnlyHourlyRates;

    return (
      <div className={styles.filters_checkbox}>
        <h1>{t('Compensations (EUR)')}</h1>

        {showBothTypes && (
          <h3 className={styles.section_header}>{t('Monthly Salary')}</h3>
        )}

        {/* Show monthly salary options by default or if non-freelance contract types are selected */}
        {showMonthlySalary &&
          getSalaryLabels(values.compensations).map((e, i) => (
            <Checkboxes
              checkboxes={e}
              onArrayChange={(e) => {
                if (Array.isArray(e)) {
                  handleCheckbox(e[0].value ?? e[0].label, 'compensations');
                }
              }}
              key={`monthly-${i}`}
            />
          ))}

        {/* Show hourly rate options with header if needed */}
        {showBothTypes && (
          <>
            <h3 className={styles.section_header}>{t('Hourly Rate')}</h3>
            {hourlyRateChoices.map((choice, i) => (
              <Checkboxes
                checkboxes={[
                  {
                    label: String(choice.to_show),
                    isChecked: values.compensations?.includes(
                      String(choice.value)
                    ),
                    value: String(choice.value)
                  }
                ]}
                onArrayChange={(e) => {
                  if (Array.isArray(e)) {
                    handleCheckbox(e[0].value ?? e[0].label, 'compensations');
                  }
                }}
                key={`hourly-${i}`}
              />
            ))}
          </>
        )}

        {/* Show only hourly rates if only freelance is selected */}
        {showOnlyHourlyRates &&
          salaryLabels.map((e, i) => (
            <Checkboxes
              checkboxes={e}
              onArrayChange={(e) => {
                if (Array.isArray(e)) {
                  handleCheckbox(e[0].value ?? e[0].label, 'compensations');
                }
              }}
              key={i}
            />
          ))}
      </div>
    );
  };

  return (
    <Formik
      className={styles.filters}
      initialValues={{}}
      onSubmit={(e) => console.log(e)}
    >
      <Form className={styles.filters}>
        <div className={styles.filters}>
          <h1>{t('Filter')}</h1>
          <div className={styles.filters_checkbox}>
            <h1 className="label">{t('Industry')}</h1>
            <Autocomplete
              fieldName="industry"
              show_arrow={true}
              value={
                industries.find(
                  (industry) => String(industry?.id) === String(values.industry)
                )?.name ?? ''
              }
              options={
                industries?.map((e) => ({
                  id: e?.id,
                  title: e?.name
                })) || []
              }
              show_location={false}
              placeholder={t('Industry')}
              onChange={(e) => {
                const activeItem = String(
                  industries.find((industry) => industry.name === e)?.id
                );
                setValues((prev) => ({
                  ...prev,
                  industry: activeItem
                }));
                onInputChange(e, 'industry');
              }}
              onInputChange={(e) => onInputChange(e, 'industry')}
              values={industries}
              onClear={handleClearIndustry}
            />
          </div>
          <div className={styles.filters_checkbox}>
            <h1 className="label">{t('Degree Type')}</h1>
            <DropDown
              onReset={resetDropdown}
              defaultValue={values.degree_type}
              placeholder={t('Degree type')}
              dropdownClassName={`dashboard__left-dropdown ${styles.filters_dropdown}`}
              items={degreeTypes}
              selectedValue={(e) =>
                setValues((prev) => ({ ...prev, degree_type: e }))
              }
              onClear={handleClearDegreeType}
            />
          </div>

          <div className={styles.filters_checkbox}>
            <h1 className="label">{t('Skills')}</h1>
            <Multiselect
              fieldName="computer_skill"
              value={values.computer_skill
                .map((id) => {
                  const skill = skills?.find((s) => String(s.id) === id);
                  return skill
                    ? { id: skill.id, title: skill.name, name: skill.name }
                    : null;
                })
                .filter(Boolean)}
              options={
                skills?.map((e: IAutocompleteItem) => ({
                  id: e?.id,
                  title: e?.name,
                  name: e?.name
                })) || []
              }
              show_location={false}
              placeholder={t('Skills')}
              onChange={(e) => {
                if (typeof e !== 'string' && Array.isArray(e)) {
                  handleComputerSkillChange(e);
                }
              }}
            />
            <div className={styles.selected_skills}>
              {values.computer_skill.map((id) => {
                const skill = skills?.find((s) => String(s.id) === id);
                return skill ? (
                  <span key={id} className={styles.selected_skill}>
                    {skill.name}
                    <span
                      onClick={() => {
                        const updatedValues = {
                          ...values,
                          computer_skill: values.computer_skill.filter(
                            (skillId) => skillId !== id
                          )
                        };
                        setValues(updatedValues);
                        applyFilter(updatedValues);
                      }}
                    >
                      <Image
                        className={styles.remove}
                        name="removes.svg"
                        width={10}
                        height={10}
                      />
                    </span>
                  </span>
                ) : null;
              })}
            </div>
          </div>

          {/* incase we later want a freesolo filter */}
          {/* here goes skills filter*/}
          {/* <div className={styles.filters_checkbox}>
            <h1 className="label">{t('Skills')}</h1>
            <Multiselect
              options={
                skills?.map((e) => ({
                  id: e?.id,
                  title: e?.name,
                  name: e?.name,
                  isComputerSkill: true
                })) || []
              }
              show_location={false}
              placeholder={t('Enter skills')}
              freeSolo={true}
              onChange={(value) => {
                if (!Array.isArray(value)) return;

                const newSkills = value
                  .map((item) => {
                    // Case 1: Direct string (from freeSolo/handleKeyPress)
                    if (typeof item === 'string') {
                      // Check if it matches a computer skill
                      const matchedSkill = skills?.find(
                        (s) => s.name.toLowerCase() === item.toLowerCase()
                      );
                      if (matchedSkill) {
                        return {
                          id: matchedSkill.id,
                          title: matchedSkill.name,
                          name: matchedSkill.name,
                          isComputerSkill: true
                        };
                      }
                      // Free-form skill
                      return {
                        id: item,
                        title: item,
                        name: item,
                        isComputerSkill: false
                      };
                    }

                    // Case 2: Object (from dropdown or existing selection)
                    if (item && typeof item === 'object') {
                      // If it's already in the correct format, return as is
                      if ('isComputerSkill' in item) {
                        return item;
                      }
                      // If it's from dropdown but missing isComputerSkill
                      if ('id' in item) {
                        return {
                          ...item,
                          isComputerSkill: true
                        };
                      }
                    }

                    // Fallback case (shouldn't happen)
                    return null;
                  })
                  .filter(Boolean);

                // Remove duplicates based on id for computer skills and title for free-form
                const uniqueSkills = newSkills.reduce((acc: any[], skill) => {
                  const isDuplicate = acc.some((existing) =>
                    skill.isComputerSkill
                      ? String(existing.id) === String(skill.id)
                      : existing.title === skill.title
                  );
                  if (!isDuplicate) {
                    acc.push(skill);
                  }
                  return acc;
                }, []);

                setValues((prev) => ({
                  ...prev,
                  skills: uniqueSkills
                }));
              }}
              onInputChange={(e) => onInputChange(e || '', 'skills')}
              value={values.skills}
              fieldName="skills"
            />

            <div className={styles.languages}>
              {values.skills?.map((skill: any, index: number) => (
                <span
                  key={skill.isComputerSkill ? skill.id : `free-${index}`}
                  className={styles.language}
                >
                  {skill.name || skill.title}
                  <span
                    onClick={() => {
                      setValues((prev) => ({
                        ...prev,
                        skills: prev.skills.filter((s: any) =>
                          skill.isComputerSkill
                            ? String(s.id) !== String(skill.id)
                            : s.title !== skill.title
                        )
                      }));
                    }}
                  >
                    <Image
                      className={styles.remove}
                      name="removes.svg"
                      width={10}
                      height={10}
                    />
                  </span>
                </span>
              ))}
            </div>
          </div> */}
          <div className={styles.filters_checkbox}>
            <h1 className="label">{t('Location')}</h1>
            <Autocomplete
              fieldName="location"
              show_arrow={true}
              value={values.location.name}
              options={
                cities?.map((e) => ({
                  id: e?.id,
                  title: e?.display_name
                })) || []
              }
              show_location={true}
              placeholder={t('Location')}
              onChange={(location) => {
                const activeItem = cities.find(
                  (cities) => cities.display_name === location
                );

                setValues({
                  ...values,
                  location: {
                    id: activeItem?.id,
                    name: activeItem?.display_name
                  }
                });
                onInputChange(location, 'location');
              }}
              onInputChange={(e) => onInputChange(e, 'location')}
              values={cities}
              loadMore={handleLoadMoreCities}
              onClear={handleClearLocation}
            />
          </div>
          <div className={styles.filters_checkbox}>
            <h1>{t('Contract Type')}</h1>
            {contractTypes.map((contract, i) => (
              <Checkboxes
                key={i}
                checkboxes={[
                  {
                    label: String(contract.to_show),
                    value: String(contract.value),
                    isChecked: values.contract.includes(String(contract.value))
                  }
                ]}
                onArrayChange={(e) => {
                  if (Array.isArray(e) && e[0] && (e[0].value || e[0].label)) {
                    const value = e[0].value ? String(e[0].value) : e[0].label;
                    handleCheckbox(value, 'contract');
                  }
                }}
              />
            ))}
          </div>
          {renderCompensationSection()}
          <div className={styles.filters_checkbox}>
            <h1>{t('Company Size')}</h1>
            {companySizeChoices.map((companySize, i) => (
              <Checkboxes
                key={i}
                checkboxes={[
                  {
                    label: String(companySize.to_show),
                    value: String(companySize.value),
                    isChecked:
                      Array.isArray(values.company_sizes) &&
                      values.company_sizes.includes(String(companySize.value))
                  }
                ]}
                onArrayChange={(e) => {
                  if (Array.isArray(e) && e[0] && (e[0].value || e[0].label)) {
                    const value = e[0].value ? String(e[0].value) : e[0].label;
                    handleCheckbox(value, 'company_sizes');
                  }
                }}
              />
            ))}
          </div>
          <div className={styles.filters_checkbox}>
            <h1>{t('Career level')}</h1>
            {getCareerLevelLabels(values.career_level).map((e, i) => (
              <Checkboxes
                checkboxes={e}
                onArrayChange={(e) => {
                  if (Array.isArray(e)) {
                    handleCheckbox(e[0].value ?? e[0].label, 'career_level');
                  }
                }}
                key={i}
              />
            ))}
          </div>
          <div
            className={styles.filters_checkbox}
            style={{ marginTop: '25px' }}
          >
            <Multiselect
              options={
                languages
                  ?.map((e) => ({
                    id: e?.id,
                    title: e?.name
                  }))
                  .filter(
                    (language) =>
                      // Filter out languages that are already selected
                      !values.languages.some(
                        (selectedLang) => selectedLang.id === language.id
                      )
                  ) || []
              }
              show_location={false}
              placeholder={t('Language')}
              onInputChange={(e) => {
                getLanguages(e ?? '');
              }}
              onChange={(e: any) => {
                if (typeof e != 'string') {
                  // Filter out duplicates based on language id
                  const uniqueLanguages = e.reduce(
                    (acc: ICandidateLanguage[], lang: ICandidateLanguage) => {
                      const isDuplicate = acc.some(
                        (item) => item.id === lang.id
                      );
                      if (!isDuplicate) {
                        acc.push(lang);
                      }
                      return acc;
                    },
                    []
                  );

                  const updatedValues = {
                    ...values,
                    languages: uniqueLanguages
                  };
                  setValues(updatedValues);
                  applyFilter(updatedValues);
                }
              }}
              value={values?.languages}
              fieldName="language"
            />

            <div className={styles.languages}>
              {values.languages.map((language: any) => {
                return (
                  <span key={language.id} className={styles.language}>
                    {language.name || language.title}
                    <span onClick={() => handleRemoveLanguage(language)}>
                      <Image
                        className={styles.remove}
                        name="removes.svg"
                        width={10}
                        height={10}
                      />
                    </span>
                  </span>
                );
              })}
            </div>
          </div>

          <div className="field ">
            <Checkboxes
              onArrayChange={(e) => {
                if (Array.isArray(e)) {
                  setValues((prev) => ({
                    ...prev,
                    is_remote: e[0].isChecked || false
                  }));
                }
              }}
              checkboxes={[
                {
                  label: t('Remote'),
                  isChecked: values?.is_remote
                }
              ]}
            />
          </div>
          <div>
            <Button
              t={t}
              className={`navbar__left-btn ${styles.filters_submit} ${styles.filters_buttons}`}
              title={t('Filter')}
              onClick={handleFilter}
            />
          </div>
          <div className={styles.filters_reset_button}>
            <Button
              t={t}
              className={`navbar__left-btn ${styles.filters_reset} ${styles.filters_buttons}`}
              title={t('Reset Filters')}
              onClick={handleResetFilters}
            />
          </div>
        </div>
      </Form>
    </Formik>
  );
};

export default JobFilters;
